import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to track write-in card usage stats and rate limits
 * Fetches current usage from backend and provides real-time stats
 */
function useWriteInStats(roomCode, playerId, currentRound) {
  const [stats, setStats] = useState({
    perRound: {
      used: 0,
      limit: 3,
      remaining: 3,
    },
    cooldown: {
      remaining: 0,
      active: false,
    },
  });
  const [limits, setLimits] = useState({
    perRound: 3,
    cooldownSeconds: 5,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch stats from backend
  const fetchStats = useCallback(async () => {
    if (!roomCode || !playerId) return;

    setLoading(true);
    setError(null);

    try {
      const API_URL =
        process.env.REACT_APP_API_URL ||
        process.env.REACT_APP_BACKEND_URL ||
        (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3001');
      const response = await fetch(`${API_URL}/api/write-in-stats/${roomCode}/${playerId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch write-in stats');
      }

      const data = await response.json();
      setStats(data.stats);
      setLimits(data.limits);
    } catch (err) {
      console.error('[useWriteInStats] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [roomCode, playerId]);

  // Fetch stats on mount and when dependencies change
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Refetch when round changes
  useEffect(() => {
    if (currentRound) {
      fetchStats();
    }
  }, [currentRound, fetchStats]);

  // Countdown timer for cooldown
  useEffect(() => {
    if (stats.cooldown.active && stats.cooldown.remaining > 0) {
      const interval = setInterval(() => {
        setStats((prev) => {
          const newRemaining = Math.max(0, prev.cooldown.remaining - 1);
          return {
            ...prev,
            cooldown: {
              remaining: newRemaining,
              active: newRemaining > 0,
            },
          };
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [stats.cooldown.active, stats.cooldown.remaining]);

  return {
    stats,
    limits,
    loading,
    error,
    refetch: fetchStats,
  };
}

export default useWriteInStats;
